import { z } from 'zod';
import { isSameDay, isSameMinute, isAfter, isBefore } from 'date-fns';

import { EventFormat, EditableEvent, EventType, T2DomainEnum } from 'types/event.types';
import { guessTimezone, parseUTCDate, getStartOfDayInTimezone } from 'utils/dateTimeUtils';
import {
  validateEventDescriptionNotEmpty,
  getEventFormatFromApi,
  convertSendAtToFormValue,
  convertSendAtToDBValue,
  createT2DomainRefinement
} from 'pages/dashboard/overview/events/schemas/commonUtils';

import { UpdateEventBasicDetailsPayloadInput } from '../../api/eventBasicDetails';

const baseSchema = z.object({
  photoUrl: z.string({ required_error: 'Photo is required' }).url(),
  title: z.string().min(1, { message: 'Title is required' }),
  description: z
    .string()
    .refine(validateEventDescriptionNotEmpty, { message: 'Description is required' }),
  isPrivate: z.boolean(),
  teamId: z.coerce
    .number({ required_error: 'Team is required', invalid_type_error: 'Team is required' })
    .positive({ message: 'Team is required' }),
  companyLocationId: z.coerce
    .number({ required_error: 'Location is required' })
    .positive()
    .nullable(),
  t2DomainArray: z.array(z.nativeEnum(T2DomainEnum)).optional().nullable()
});

const timingSchema = z.object({
  startDateTime: z.date({ required_error: 'Start date and time are required' }),
  endDateTime: z.date({ required_error: 'End date and time are required' }),
  allDay: z.boolean(),
  timezoneCode: z.string({ required_error: 'Timezone is required' })
});

const formatSchema = z.object({
  format: z.nativeEnum(EventFormat, { required_error: 'Format is required' }),
  virtualLink: z.string().nullable(),
  zoomMeetingId: z.number().optional(),
  googleMeetingId: z.number().optional(),
  inPersonLocation: z.string().nullable()
});

const singleEventSchema = baseSchema.merge(timingSchema).merge(formatSchema);

const multiSessionEventSchema = baseSchema.extend({
  timezoneCode: z.string({ required_error: 'Timezone is required' })
});

interface SchemaFactoryOptions {
  t2DomainEnabled?: boolean;
}

const editEventDetailsSchemaFactory = (
  event: EditableEvent,
  options: SchemaFactoryOptions = {}
) => {
  const { t2DomainEnabled } = options;
  const t2DomainRefinement = createT2DomainRefinement(!!t2DomainEnabled);

  if (event.eventType === EventType.MULTI_SESSIONS) {
    return multiSessionEventSchema.superRefine(t2DomainRefinement);
  }

  return singleEventSchema.superRefine(t2DomainRefinement).superRefine((data, ctx) => {
    // Event schedule validations
    // Note: Form can't be updated once the event has ended (past event). So we don't need to validate against that.

    // Convert form dates (in browser timezone) to UTC for comparison
    const startDateTimeUTC = convertSendAtToDBValue(data.startDateTime, data.timezoneCode);
    const startDateTime = parseUTCDate(startDateTimeUTC);

    if (event.hasStarted && event.datetimeScheduled) {
      // If event has started, we need to check if the start date and time have been changed
      const startDateTimeUTCDate = parseUTCDate(startDateTimeUTC);
      const originalStartDateTimeUTCDate = parseUTCDate(event.datetimeScheduled);

      if (!isSameMinute(startDateTimeUTCDate, originalStartDateTimeUTCDate)) {
        // Start date and time cannot be changed once the event has started
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date and time cannot be changed once the event has started',
          path: ['startDateTime']
        });
      }
    } else {
      // For all-day events: compare with start of today in event's timezone
      // For regular events: compare with current time
      const timeToCompare = data.allDay ? getStartOfDayInTimezone(data.timezoneCode) : new Date();
      if (isBefore(startDateTime, timeToCompare)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date and time must be in the future',
          path: ['startDateTime']
        });
      }
    }

    if (data.allDay) {
      if (
        !isSameDay(data.endDateTime, data.startDateTime) &&
        !isAfter(data.endDateTime, data.startDateTime)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End date must be same or after start date',
          path: ['endDateTime']
        });
      }
    } else if (
      isSameMinute(data.endDateTime, data.startDateTime) ||
      !isAfter(data.endDateTime, data.startDateTime)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date and time must be after start date and time',
        path: ['endDateTime']
      });
    }
  });
};

type EditEventDetailsFormValue = z.infer<typeof singleEventSchema & typeof multiSessionEventSchema>;

const getFormValueFromApi = (event: EditableEvent): Partial<EditEventDetailsFormValue> => ({
  photoUrl: event.photoUrl ?? '',
  title: event.title ?? '',
  /**
   * 1. Remove div tags that are added by sanitizing in backend.
   * 2. Remove trailing empty lines (<p><br></p>) that are cleaned by QuillJs.
   *
   * This prevents a save mismatch since quill will remove these anyways
   * and then we'll detect a change has been made (when it actually hasn't)
   * TODO: Find a cleaner solution (with quill?)
   */
  description:
    typeof event.description === 'string'
      ? event.description.replace(/^<div>|<\/div>$/g, '').replace(/(<p><br><\/p>)+$/g, '')
      : event.description ?? '',
  startDateTime: event.datetimeScheduled
    ? convertSendAtToFormValue(event.datetimeScheduled, event.timezoneCode ?? guessTimezone())
    : undefined,
  endDateTime: event.datetimeEnd
    ? convertSendAtToFormValue(event.datetimeEnd, event.timezoneCode ?? guessTimezone())
    : undefined,
  isPrivate: event.isPrivate,
  allDay: event.allDay,
  format: getEventFormatFromApi(event),
  virtualLink: event.virtualEventInfo?.virtualLocation ?? null,
  inPersonLocation: event.locationAddress ?? null,
  timezoneCode: event.timezoneCode,
  zoomMeetingId: event.zoomMeeting?.id,
  googleMeetingId: event.googleMeeting?.id,
  /**
   * ComboBox and Select components are designed to work with string values only.
   * If you pass numbers or other types, React Hook Form's isDirty and value comparison logic will break,
   * causing subtle bugs (e.g., fields always appearing dirty or not updating correctly).
   * To avoid this, we explicitly coerce any initial value (like IDs) to a string here.
   * This is a workaround for the lack of native type support in these UI primitives.
   * If you ever refactor these fields to support numbers, remove this hack and update the form logic accordingly.
   */
  companyLocationId: event.companyLocation?.id
    ? (String(event.companyLocation?.id) as unknown as number)
    : null,
  teamId: event.hostOrganization?.id
    ? (String(event.hostOrganization?.id) as unknown as number)
    : undefined,
  t2DomainArray: event.t2DomainArray ?? undefined
});

const formatSingleEventFormValuesForApi = (
  eventId: number,
  formValues: EditEventDetailsFormValue
): UpdateEventBasicDetailsPayloadInput => {
  const isVirtual =
    formValues.format === EventFormat.HYBRID || formValues.format === EventFormat.VIRTUAL;
  const isInPerson =
    formValues.format === EventFormat.HYBRID || formValues.format === EventFormat.IN_PERSON;

  return {
    type: 'single',
    id: eventId,
    title: formValues.title,
    photoUrl: formValues.photoUrl,
    description: formValues.description,
    datetimeScheduled: convertSendAtToDBValue(formValues.startDateTime, formValues.timezoneCode),
    datetimeEnd: convertSendAtToDBValue(formValues.endDateTime, formValues.timezoneCode),
    timezoneCode: formValues.timezoneCode,
    allDay: formValues.allDay,
    isVirtual,
    isInPerson,
    locationAddress: isInPerson ? formValues.inPersonLocation ?? undefined : undefined,
    virtualLink: isVirtual ? formValues.virtualLink ?? undefined : undefined,
    zoomMeetingId: isVirtual ? formValues.zoomMeetingId : undefined,
    googleMeetingId: isVirtual ? formValues.googleMeetingId : undefined,
    isPrivate: formValues.isPrivate,
    teamId: formValues.teamId,
    companyLocationId: formValues.companyLocationId ?? undefined,
    // Don't send empty array to backend, send undefined instead
    t2DomainArray:
      formValues.t2DomainArray && formValues.t2DomainArray.length > 0
        ? formValues.t2DomainArray
        : undefined
  };
};

const formatMultiSessionEventFormValuesForApi = (
  eventId: number,
  formValues: EditEventDetailsFormValue
): UpdateEventBasicDetailsPayloadInput => ({
  type: 'multi-sessions',
  id: eventId,
  photoUrl: formValues.photoUrl,
  title: formValues.title,
  description: formValues.description,
  isPrivate: formValues.isPrivate,
  teamId: formValues.teamId,
  companyLocationId: formValues.companyLocationId ?? undefined,
  timezoneCode: formValues.timezoneCode,
  // Don't send empty array to backend, send undefined instead
  t2DomainArray:
    formValues.t2DomainArray && formValues.t2DomainArray.length > 0
      ? formValues.t2DomainArray
      : undefined
});

export {
  editEventDetailsSchemaFactory,
  getFormValueFromApi,
  formatSingleEventFormValuesForApi,
  formatMultiSessionEventFormValuesForApi,
  type EditEventDetailsFormValue
};
